import { ProductCard } from "@/components/blocks/HelloRetailProductCard";
import { useHelloRetailProductRecommendations } from "@/data/Content/HelloRetailProductRecommendations";
import { ID } from "@/data/types/Basic";
import { HelloretailProductType } from "@/data/types/HelloRetail";
import { WidgetProperties } from "@/data/types/Slot";
import { FC, useEffect, useId, useMemo, useState } from "react";
import { cardNameSx, carouselSlideSX } from '@/components/content/CatalogEntryRecommendation/styles/carouselSlide';
import { CarouselSlider } from "@/components/blocks/Carousel";
import { CarouselOptions } from "@/data/types/Carousel";
import { DEFAULT_CAROUSEL_OPTS } from "@/data/constants/carousel";
import { useMediaQuery, useTheme } from "@mui/system";
import { Box } from "@mui/material";
import { cardImageSx } from "@/components/content/HelloRetailProductRecommendations/styles/container";
import { getVisibleSlidesLength } from "@/utils/carouselSlidesConfig";

const cardContainerMapper = (
    products: HelloretailProductType[],
    clickAction?: (trackingCode: string) => void
) =>
    products.map((product, index) => ({
        product,
        card: (
            <ProductCard
                key={`${product.partNumber}_${index}`}
                product={product}
                clickAction={clickAction?.bind(null, product.trackingCode)}
                styles={{ cardNameSx, cardImageSx }}
            />
        ),
        a11y: { 'aria-label': undefined, 'aria-labelledby': product.partNumber },
    }));

const emptyProperties = {} as WidgetProperties;

export const HelloRetailProductRecommendations: FC<{ id: ID; properties?: WidgetProperties }> = ({ id: _id, properties = emptyProperties }) => {
    
    const { carouselContainerWidth = "100" } = properties;

    const { products, helloRetailClickTracking } = useHelloRetailProductRecommendations(properties, _id);
    
    const original = useMemo(
        () => cardContainerMapper(products, helloRetailClickTracking),
        [products, helloRetailClickTracking]
    );
    
    const { slides, a11yProps } = useMemo(() => {
        const slides = original.map(({ card }) => card);
        const a11yProps = original.map(({ a11y }) => a11y);
        return { slides, a11yProps };
    }, [original]);

    const theme = useTheme();
    const lgMatch = useMediaQuery(theme.breakpoints.down('lg'));
    const mdMatch = useMediaQuery(theme.breakpoints.down('md'));
    const smMatch = useMediaQuery(theme.breakpoints.down('sm'));
    
    const containerId = `el-${useId().replace(/:/g, "_")}`;
    const isNotFullWidth = carouselContainerWidth !== "100";
    const [isMobileHalfWidth, setMobileHalfWidth] = useState(false);
    useEffect(() => {
        if (!smMatch || isMobileHalfWidth) return;
        // This is to handle special scenario in IntertoysActiesAndFolderLayout3_rv layout
        // where a grid item has 9/12 width in desktop
        // and 6/12 in mobile (general case would be 12/12 in mobile)
        //xs={6} sm={3}
        const parentClasses = document.querySelector(`#${containerId}`)?.parentElement?.classList;
        if (parentClasses?.contains("MuiGrid-grid-sm-6")) setMobileHalfWidth(true);
    }, [containerId, smMatch, isMobileHalfWidth]);

    const visibleSlides = useMemo(() => {
        if (smMatch) return isNotFullWidth && !isMobileHalfWidth ? 1 : 2.1;
        if (mdMatch) return isNotFullWidth ? getVisibleSlidesLength(carouselContainerWidth, 3, "md") : 4;
        return isNotFullWidth ? getVisibleSlidesLength(carouselContainerWidth, 4) : 4;
    }, [isNotFullWidth, smMatch, mdMatch, lgMatch, carouselContainerWidth, isMobileHalfWidth]);

    const carouselProps: CarouselOptions = useMemo(
            () => ({
                ...DEFAULT_CAROUSEL_OPTS,
                visibleSlides,
                step: visibleSlides,
                totalSlides: slides.length,
            }),
            [slides.length, visibleSlides]
        );
    
    

    return slides?.length ? (
        <Box 
            id={containerId}
            className={
                isNotFullWidth 
                ? 'hello-retail-product-recommendations slider-not-full-width'
                : 'hello-retail-product-recommendations'
            }
        >
            {slides?.length ? 
                <CarouselSlider
                    a11yProps={a11yProps}
                    slides={slides}
                    carouselProps={carouselProps}
                    carouselSlideStyles={carouselSlideSX}
                    hideNavButtons={slides?.length <= visibleSlides}
                ></CarouselSlider>
            :null}
        </Box>
    ) : null;
}